import Portal from "@arcgis/core/portal/Portal.js";
import OAuthInfo from "@arcgis/core/identity/OAuthInfo.js";
import esriId from "@arcgis/core/identity/IdentityManager.js";
import { AppConfig } from "../AppConfig";
import esriConfig from "@arcgis/core/config.js";
import esriRequest from "@arcgis/core/request.js";
import type { RequestResponse } from "@arcgis/core/request/types";

let _portal: Portal | null = null;
let _info: OAuthInfo | null = null;

const getPortalUrl = () => AppConfig.ArcGIS.BaseUrl;

const getPortal = () => {
    _portal ??= new Portal({
        url: getPortalUrl(),
    });
    return _portal;
};

const getInfo = () => {
    _info ??= new OAuthInfo({
        portalUrl: getPortalUrl(),
        appId: AppConfig.ArcGIS.AppClientId,
        flowType: "auto", // default that uses two-step flow
        popup: false,
    });
    return _info;
};

const getPortalUser = async () => {
    const portal = getPortal();
    await portal.load();
    return portal.user;
};

// Sign out is having issues on deployed app
export const signOut = async () => {
    esriId.destroyCredentials();
    const portal = getPortal();
    portal.destroy();

    try {
        const info = getInfo();
        const credential = await esriId.checkSignInStatus(`${info.portalUrl}/sharing`);
        if (credential) {
            // If we still have credentials, destroy them again
            esriId.destroyCredentials();
            portal.destroy();

            // Wait 100ms before checking again
            await new Promise((resolve) => setTimeout(resolve, 100));

            await esriId.checkSignInStatus(`${info.portalUrl}/sharing`).catch(() => {
                window.location.reload();
            });
        }
    } catch (error) {
        // Sign-out was successful, reload the page
        window.location.reload();
    }
};

export const getUser = async (selfSetter?: (self: any) => void) => {
    const info = getInfo();
    esriId.registerOAuthInfos([info]);
    esriId.on("credential-create", () => {
        getSelf().then((res: RequestResponse<unknown>) => {
            if (selfSetter && res.data) {
                selfSetter(res.data);
            }
        });
    });
    await signIn();
    return await getPortalUser();
};

const checkCurrentStatus = async () => {
    try {
        const info = getInfo();
        const credential = await esriId.checkSignInStatus(`${info.portalUrl}/sharing`);
        return credential;
    } catch (error) {
        console.warn(error);
    }
};

const signIn = async () => {
    try {
        const credential = (await checkCurrentStatus()) || (await fetchCredentials());
        return credential;
    } catch (error) {
        const credential = await fetchCredentials();
        return credential;
    }
};

const fetchCredentials = async () => {
    try {
        const info = getInfo();
        const credential = await esriId.getCredential(`${info.portalUrl}/sharing`);
        return credential;
    } catch (error) {
        console.warn(error);
    }
};

export const getAGEToken = async (): Promise<string> => {
    try {
        const credential = await fetchCredentials();
        if (credential) {
            esriConfig.apiKey = credential.token;
            return credential.token;
        }
    } catch (error) {
        console.warn(error);
    }
    return "";
};

export const getSelf = async () => {
    const info = getInfo();
    return esriRequest(`${info.portalUrl}/sharing/rest/community/self`, {
        method: "auto",
        responseType: "json",
        query: {
            f: "json",
        },
    });
};
